import { ConsoleContinue } from "components/platform/companyConsole/ConsoleState";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import dayjs from "utils/dayjs";
import { Box } from "@mui/material";
import { ConsoleError } from "components/platform/companyConsole/ConsoleState";
import { useCompanyConnection } from "hooks/useCompanyConnection";
import { DataTable, toast } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { getUserFacingErrorMessage } from "utils/graphql";
import {
  GET_PLATFORM_WEBHOOK_DELIVERIES,
  REPLAY_PLATFORM_WEBHOOK_EVENT,
} from "graphql/webhookDeliveries";
import WebhookDeliveryDrawer, {
  WebhookDeliveryStatusChip,
} from "./WebhookDeliveryDrawer";
import {
  DELIVERY_TRIGGER_LABEL,
  extractWebhookDeliveries,
  type WebhookDelivery,
} from "./webhookDeliveriesUtils";

type WebhookDeliveriesProps = {
  endpointId: string | null;
  selectedCompanyId?: string;
  eventId?: string;
  resourceId?: string;
  startDate?: number;
  endDate?: number;
  endpointsLoading: boolean;
};

const WebhookDeliveries: React.FC<WebhookDeliveriesProps> = ({
  endpointId,
  endpointsLoading,
  selectedCompanyId,
  eventId,
  resourceId,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<WebhookDelivery | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refetchIndex, setRefetchIndex] = useState(0);
  const [replayEvent, { loading: replaying }] = useMutation(
    REPLAY_PLATFORM_WEBHOOK_EVENT,
  );

  const initialVariables = useMemo(
    () => ({ webhookEndpointId: endpointId, first: 25, after: undefined }),
    [endpointId],
  );

  const { dataArray, displayLoading, handleScroll } = useInfiniteScroll({
    query: GET_PLATFORM_WEBHOOK_DELIVERIES,
    initialVariables,
    refetchIndex,
    skip: !endpointId || !!selectedCompanyId,
    extractData: extractWebhookDeliveries,
  });

  const scoped = useCompanyConnection<WebhookDelivery>({
    query: GET_PLATFORM_WEBHOOK_DELIVERIES,
    variables: {
      ...initialVariables,
      selectedCompanyId,
      eventId,
      resourceId,
      startDate,
      endDate,
    },
    field: "platformWebhookDeliveries",
    skip: !selectedCompanyId || !endpointId,
    refresh: refetchIndex,
  });
  const rows = endpointId
    ? selectedCompanyId
      ? scoped.rows
      : (dataArray as WebhookDelivery[])
    : [];

  const columns: DataColumn<WebhookDelivery>[] = useMemo(
    () => [
      {
        id: "created",
        header: t("Created"),
        cell: (row) => formatCell(row.createdAt),
      },
      {
        id: "event",
        header: t("Event"),
        cell: (row) => row.eventType ?? "-",
      },
      {
        id: "trigger",
        header: t("Trigger"),
        cell: (row) => t(DELIVERY_TRIGGER_LABEL[row.trigger]),
      },
      {
        id: "status",
        header: t("Status"),
        cell: (row) => <WebhookDeliveryStatusChip status={row.status} />,
      },
      {
        id: "attempts",
        header: t("Attempts"),
        align: "right",
        cell: (row) => row.attemptCount,
      },
      {
        id: "nextAttempt",
        header: t("Next attempt"),
        cell: (row) => formatCell(row.nextAttemptAt),
      },
    ],
    [t],
  );

  const handleReplay = async (delivery: WebhookDelivery) => {
    try {
      await replayEvent({
        variables: {
          webhookEndpointId: delivery.webhookEndpointId,
          eventId: delivery.eventId,
          selectedCompanyId,
        },
      });
      toast.success(t("EventReplayQueued"));
      setDrawerOpen(false);
      setRefetchIndex((index) => index + 1);
    } catch (error) {
      toast.error(getUserFacingErrorMessage(error, t("EventReplayFailed")));
    }
  };

  return (
    <Box ref={scoped.viewportRef} sx={{ flex: 1, minHeight: 0 }}>
      {scoped.error && <ConsoleError retry={scoped.retry} />}
      <ConsoleContinue paused={scoped.paused} loadMore={scoped.loadMore} />
      <DataTable<WebhookDelivery>
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.id}
        loading={
          (selectedCompanyId ? scoped.loading : displayLoading) ||
          endpointsLoading
        }
        isFetchingMore={selectedCompanyId ? scoped.isFetchingMore : false}
        onRowClick={(row) => {
          setSelected(row);
          setDrawerOpen(true);
        }}
        onScroll={selectedCompanyId ? scoped.handleScroll : handleScroll}
        emptyState={
          endpointId ? t("NoWebhookDeliveries") : t("NoWebhookEndpoints")
        }
        stickyHeader
      />
      <WebhookDeliveryDrawer
        delivery={selected}
        open={drawerOpen}
        replaying={replaying}
        onClose={() => setDrawerOpen(false)}
        onAfterClose={() => setSelected(null)}
        onReplay={handleReplay}
      />
    </Box>
  );
};

function formatCell(instant: string | null) {
  return instant ? dayjs(instant).format("YYYY/MM/DD HH:mm:ss") : "-";
}

export default WebhookDeliveries;
